import React, { useEffect, useRef, useState } from "react";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";

import { ButtonVariant } from "Constant/button";
import { connectionSectionDeleteButtonSx } from "pages/LeadsPages/shared/connectionSectionActionStyles";
import ConnectionSectionDescription from "pages/LeadsPages/shared/ConnectionSectionDescription";
import { useSelector } from "react-redux";
import ClipLoader from "react-spinners/ClipLoader";

import EmptyState from "components/atoms/EmptyState";
import StandartButton from "components/atoms/StandartButton";

import {
    DeleteCol,
    FilterArea,
    FilterHeader,
    FilterListItem,
    FilterListLeft,
    FilterListRight,
    FilterListWrapper,
    MobileFilterRow,
    SaveButton,
    SaveButtonWrapper,
} from "./LeadConnectionFilter.styled";

// Constants
const IS_NOT_EMPTY_CONDITION = "isNotEmpty";

// Helper functions
const isNotEmptyCondition = (condition) => condition === IS_NOT_EMPTY_CONDITION;

const isEmptyValue = (val) => val === "" || val === null || val === undefined;

const LeadConnectionFilter = (props) => {
    const language = useSelector((state) => state.languagesState.languages);
    const isWizardMode = props?.isWizard === true;
    const [filterShowStatus, setFilterShowStatus] = useState(
        props?.filterShowStatus ?? false,
    );
    const [isWizard, setIsWizard] = useState(isWizardMode);
    const [selectedOperator, setSelectedOperator] = useState("");
    const [filterList, setFilterList] = useState([]);
    const [validationStatus, setValidationStatus] = useState([]);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const filterComparings = (comparings) => {
        return comparings.map((item) => {
            if (isNotEmptyCondition(item.condition)) {
                return {
                    field: item.field,
                    condition: item.condition,
                };
            }
            return item;
        });
    };

    const handleFilterShowStatus = () => {
        if (filterShowStatus === true) {
            const restoredFilterList = props?.filters ?? [];
            setFilterList(restoredFilterList);
            setSelectedOperator(
                restoredFilterList.length > 1
                    ? (props?.selectedOperator ?? "")
                    : "",
            );
            if (props?.onClose !== undefined) {
                props?.onClose();
            }
            props?.setFilterShowStatus?.(false);
            if (isWizard) {
                props?.handleSave?.({ operator: "", comparings: [] });
            }
        }
        setShowValidationErrors(false);
        setFilterShowStatus(!filterShowStatus);
    };

    const handleFilterAddItem = () => {
        setShowValidationErrors(false);
        const updatedFilterList = [
            ...filterList,
            {
                field: null,
                value: null,
                condition: null,
            },
        ];
        setFilterList(updatedFilterList);
        if (isWizard) {
            const filteredComparings = filterComparings(updatedFilterList);
            props?.handleSave({
                operator: selectedOperator,
                comparings: filteredComparings,
            });
        }
    };

    const handleDeleteRow = (index) => {
        const updatedFilterList = filterList.filter((_, i) => i !== index);
        const updatedOperator =
            updatedFilterList.length > 1 ? selectedOperator : "";
        setFilterList(updatedFilterList);
        setSelectedOperator(updatedOperator);
        if (isWizard) {
            const filteredComparings = filterComparings(updatedFilterList);
            props?.handleSave({
                operator: updatedOperator,
                comparings: filteredComparings,
            });
            if (updatedFilterList.length === 0) {
                setFilterShowStatus(false);
            }
        }
    };

    const handleSave = () => {
        if (props?.handleSave !== undefined) {
            setShowValidationErrors(true);
            if (selectedOperator === "" && filterList.length > 1) {
                return;
            }

            const hasEmptyFields = filterList?.some((item) => {
                const isValueRequired = !isNotEmptyCondition(item?.condition);
                return (
                    isEmptyValue(item?.field) ||
                    isEmptyValue(item?.condition) ||
                    (isValueRequired && isEmptyValue(item?.value))
                );
            });

            if (hasEmptyFields) {
                return;
            }

            setShowValidationErrors(false);
            const filteredComparings = filterComparings(filterList);
            props?.handleSave({
                operator: selectedOperator,
                comparings: filteredComparings,
            });
        }
    };

    useEffect(() => {
        if (props?.filters !== undefined) {
            setFilterList(props?.filters);
            setSelectedOperator(
                (props?.filters?.length ?? 0) > 1
                    ? (props?.selectedOperator ?? "")
                    : "",
            );
        } else if (props?.selectedOperator !== undefined) {
            setSelectedOperator(props?.selectedOperator);
        }
        if (props?.isWizard !== undefined) {
            setIsWizard(props?.isWizard === true);
        }
    }, [props?.filters, props?.selectedOperator, props?.isWizard]);

    useEffect(() => {
        if (props?.triggerValidation) {
            setShowValidationErrors(true);
        }
    }, [props?.triggerValidation]);

    useEffect(() => {
        if (props?.isWizard === true) {
            return;
        }
        if (props?.filterShowStatus !== undefined) {
            setFilterShowStatus(props?.filterShowStatus);
        }
    }, [props?.filterShowStatus, props?.isWizard]);

    useEffect(() => {
        if (filterList?.length > 0) {
            let lastItem = [...filterList][[...filterList].length - 1];
            const isValueRequired = !isNotEmptyCondition(lastItem?.condition);
            const isValueValid =
                !isValueRequired || !isEmptyValue(lastItem?.value);

            if (
                lastItem &&
                (isEmptyValue(lastItem?.field) ||
                    !isValueValid ||
                    isEmptyValue(lastItem?.condition) ||
                    ([...filterList].length > 1
                        ? selectedOperator === ""
                        : false))
            ) {
                setValidationStatus(false);
            } else {
                setValidationStatus(true);
            }
        }
    }, [filterList, selectedOperator]);

    useEffect(() => {
        if (props?.setValidationStatus !== undefined) {
            props?.setValidationStatus(validationStatus);
        }
    }, [props, validationStatus]);

    useEffect(() => {
        if (
            props?.setFilterShowStatus !== undefined &&
            filterShowStatus === true
        ) {
            props?.setFilterShowStatus(filterShowStatus);
        }
    }, [filterShowStatus]);

    return (
        <FilterArea
            className={`tour-filter-section ${isWizard ? "is-wizard" : ""}`}
        >
            <Box sx={{ width: "100%", mb: 2.5 }}>
                <FilterHeader style={{ marginBottom: 0 }}>
                    <div className="filter-header-main">
                        {isWizard ? (
                            <div className="wizard-title-area">
                                <p>
                                    <strong>
                                        {language?.lead_connection_filter
                                            ?.filter ?? "Filter"}
                                    </strong>
                                    {props?.description ??
                                        "Control which leads are sent by applying conditions on source fields."}
                                </p>
                            </div>
                        ) : (
                            <>
                                <span className="filter-section-title">
                                    {language?.lead_connection_filter?.filter ??
                                        "Filter"}
                                </span>
                                <ConnectionSectionDescription>
                                    {props?.description ??
                                        "Control which leads are sent by applying conditions on source fields."}
                                </ConnectionSectionDescription>
                            </>
                        )}
                    </div>
                    {filterShowStatus ? (
                        <StandartButton
                            data-cy="lead-filter-cancel"
                            variant={ButtonVariant.BLACK_OUTLINED}
                            onClick={handleFilterShowStatus}
                        >
                            {language?.lead_connection_filter?.cancel ??
                                "Cancel"}
                        </StandartButton>
                    ) : filterList.length > 0 ? (
                        <a
                            style={{ padding: "10px" }}
                            href="javascript:;"
                            onClick={handleFilterShowStatus}
                        >
                            <EditOutlinedIcon />
                        </a>
                    ) : (
                        <StandartButton
                            data-cy="lead-filter-add-first"
                            variant={ButtonVariant.BLACK_OUTLINED}
                            onClick={() => {
                                handleFilterAddItem();
                                setFilterShowStatus(true);
                            }}
                            startIcon={<AddRoundedIcon />}
                        >
                            {language?.lead_connection_filter?.add_row ??
                                "Add your first filter"}
                        </StandartButton>
                    )}
                </FilterHeader>
            </Box>

            {filterList && filterList.length === 0 ? (
                <>
                    {filterShowStatus && (
                        <FilterListWrapper className="filter-list-wrapper active">
                            <FilterListLeft>
                                <EmptyState
                                    compact
                                    flush
                                    title={
                                        language?.lead_connection_filter
                                            ?.no_filters_left ??
                                        "No filters left"
                                    }
                                    description={
                                        language?.lead_connection_filter
                                            ?.no_filters_left_desc ??
                                        "All conditions were removed. Save to send every lead through, or add a filter to narrow it down."
                                    }
                                    actionLabel={
                                        language?.lead_connection_filter
                                            ?.add_row ?? "Add Row"
                                    }
                                    onAction={handleFilterAddItem}
                                />
                            </FilterListLeft>
                            {!isWizard && (
                                <SaveButtonWrapper>
                                    {props?.isSaveLoading && (
                                        <div className="circle-loader">
                                            <ClipLoader
                                                color={"#000"}
                                                size={17}
                                            />
                                        </div>
                                    )}
                                    <SaveButton
                                        type="button"
                                        onClick={handleSave}
                                        color="primary"
                                        variant="contained"
                                    >
                                        {language?.lead_connection_filter
                                            ?.save_changes ?? "Save Changes"}
                                    </SaveButton>
                                </SaveButtonWrapper>
                            )}
                        </FilterListWrapper>
                    )}
                </>
            ) : (
                <>
                    <div className="lead-connection-filter-content">
                        <FilterListWrapper
                            className={`filter-list-wrapper ${
                                filterShowStatus ? "active" : ""
                            }`}
                        >
                            <FilterListLeft>
                                {filterList &&
                                    filterList.map((item, index) => (
                                        <FilterListItem
                                            data-cy="lead-filter-row"
                                            key={index}
                                            className={`filter-list-item ${
                                                index + 1 ===
                                                    filterList.length &&
                                                !filterShowStatus
                                                    ? ""
                                                    : "mb-4"
                                            }`}
                                        >
                                            <MobileFilterRow
                                                style={{ width: "100%" }}
                                            >
                                                <div className="row">
                                                    <div className="col-md-4 col-sm-12">
                                                        <FormControl
                                                            style={{
                                                                width: "100%",
                                                            }}
                                                        >
                                                            <Autocomplete
                                                                options={
                                                                    props?.fields?.filter(
                                                                        (
                                                                            field,
                                                                        ) =>
                                                                            field.value !==
                                                                            "-",
                                                                    ) ?? []
                                                                }
                                                                getOptionLabel={(
                                                                    option,
                                                                ) =>
                                                                    option?.label ??
                                                                    ""
                                                                }
                                                                isOptionEqualToValue={(
                                                                    option,
                                                                    value,
                                                                ) =>
                                                                    option?.value ===
                                                                    value?.value
                                                                }
                                                                value={
                                                                    props?.fields?.find(
                                                                        (
                                                                            field,
                                                                        ) =>
                                                                            field.value ===
                                                                            item.field,
                                                                    ) ?? null
                                                                }
                                                                onChange={(
                                                                    _,
                                                                    newValue,
                                                                ) => {
                                                                    const fieldValue =
                                                                        newValue?.value ??
                                                                        null;
                                                                    setFilterList(
                                                                        (
                                                                            prevFilterList,
                                                                        ) =>
                                                                            prevFilterList.map(
                                                                                (
                                                                                    filterItem,
                                                                                    i,
                                                                                ) =>
                                                                                    i ===
                                                                                    index
                                                                                        ? {
                                                                                              ...filterItem,
                                                                                              field: fieldValue,
                                                                                          }
                                                                                        : filterItem,
                                                                            ),
                                                                    );
                                                                    if (
                                                                        isWizard
                                                                    ) {
                                                                        const updatedFilterList =
                                                                            [
                                                                                ...filterList,
                                                                            ].map(
                                                                                (
                                                                                    filterItem,
                                                                                    i,
                                                                                ) =>
                                                                                    i ===
                                                                                    index
                                                                                        ? {
                                                                                              ...filterItem,
                                                                                              field: fieldValue,
                                                                                          }
                                                                                        : filterItem,
                                                                            );
                                                                        const filteredComparings =
                                                                            filterComparings(
                                                                                updatedFilterList,
                                                                            );
                                                                        props?.handleSave(
                                                                            {
                                                                                operator:
                                                                                    selectedOperator,
                                                                                comparings:
                                                                                    filteredComparings,
                                                                            },
                                                                        );
                                                                    }
                                                                }}
                                                                disabled={
                                                                    !filterShowStatus
                                                                }
                                                                className={`w-full ${
                                                                    !filterShowStatus
                                                                        ? "text-disabled"
                                                                        : ""
                                                                }`}
                                                                renderInput={(
                                                                    params,
                                                                ) => (
                                                                    <TextField
                                                                        {...params}
                                                                        data-cy="lead-filter-field"
                                                                        label={
                                                                            language
                                                                                ?.lead_connection_filter
                                                                                ?.field ??
                                                                            "Field"
                                                                        }
                                                                        error={
                                                                            showValidationErrors &&
                                                                            isEmptyValue(
                                                                                item.field,
                                                                            )
                                                                        }
                                                                        sx={
                                                                            !filterShowStatus
                                                                                ? {
                                                                                      "& .MuiInputBase-input":
                                                                                          {
                                                                                              WebkitTextFillColor:
                                                                                                  "rgba(0, 0, 0, 0.38) !important",
                                                                                              color: "rgba(0, 0, 0, 0.38) !important",
                                                                                          },
                                                                                      "& .MuiOutlinedInput-notchedOutline":
                                                                                          {
                                                                                              borderColor:
                                                                                                  "rgba(0, 0, 0, 0.26) !important",
                                                                                          },
                                                                                  }
                                                                                : undefined
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                        </FormControl>
                                                    </div>
                                                    <div className="col-md-4 col-sm-12">
                                                        <FormControl
                                                            style={{
                                                                width: "100%",
                                                            }}
                                                        >
                                                            <Select
                                                                data-cy="lead-filter-condition"
                                                                label={
                                                                    language
                                                                        ?.lead_connection_filter
                                                                        ?.condition ??
                                                                    "Condition"
                                                                }
                                                                onChange={(
                                                                    e,
                                                                ) => {
                                                                    const newCondition =
                                                                        e
                                                                            ?.target
                                                                            ?.value;
                                                                    setFilterList(
                                                                        (
                                                                            prevFilterList,
                                                                        ) =>
                                                                            prevFilterList.map(
                                                                                (
                                                                                    item,
                                                                                    i,
                                                                                ) =>
                                                                                    i ===
                                                                                    index
                                                                                        ? {
                                                                                              ...item,
                                                                                              condition:
                                                                                                  newCondition,
                                                                                              value: isNotEmptyCondition(
                                                                                                  newCondition,
                                                                                              )
                                                                                                  ? ""
                                                                                                  : item.value,
                                                                                          }
                                                                                        : item,
                                                                            ),
                                                                    );
                                                                    if (
                                                                        isWizard
                                                                    ) {
                                                                        const updatedFilterList =
                                                                            [
                                                                                ...filterList,
                                                                            ].map(
                                                                                (
                                                                                    item,
                                                                                    i,
                                                                                ) =>
                                                                                    i ===
                                                                                    index
                                                                                        ? {
                                                                                              ...item,
                                                                                              condition:
                                                                                                  newCondition,
                                                                                              value: isNotEmptyCondition(
                                                                                                  newCondition,
                                                                                              )
                                                                                                  ? ""
                                                                                                  : item.value,
                                                                                          }
                                                                                        : item,
                                                                            );
                                                                        const filteredComparings =
                                                                            filterComparings(
                                                                                updatedFilterList,
                                                                            );
                                                                        props?.handleSave(
                                                                            {
                                                                                operator:
                                                                                    selectedOperator,
                                                                                comparings:
                                                                                    filteredComparings,
                                                                            },
                                                                        );
                                                                    }
                                                                }}
                                                                value={
                                                                    item.condition
                                                                }
                                                                disabled={
                                                                    !filterShowStatus
                                                                }
                                                                className="w-full"
                                                                error={
                                                                    showValidationErrors &&
                                                                    isEmptyValue(
                                                                        item.condition,
                                                                    )
                                                                }
                                                            >
                                                                {props?.functions &&
                                                                    props?.functions.map(
                                                                        (
                                                                            item,
                                                                        ) => (
                                                                            <MenuItem
                                                                                key={
                                                                                    item.code
                                                                                }
                                                                                value={
                                                                                    item.code
                                                                                }
                                                                            >
                                                                                {
                                                                                    item.description
                                                                                }
                                                                            </MenuItem>
                                                                        ),
                                                                    )}
                                                            </Select>
                                                        </FormControl>
                                                    </div>
                                                    {!isNotEmptyCondition(
                                                        item.condition,
                                                    ) && (
                                                        <div className="col-md-3 col-sm-12">
                                                            <FormControl
                                                                style={{
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                <TextField
                                                                    data-cy="lead-filter-value"
                                                                    label={
                                                                        language
                                                                            ?.lead_connection_filter
                                                                            ?.value ??
                                                                        "Value"
                                                                    }
                                                                    variant="outlined"
                                                                    className={`w-full ${
                                                                        !filterShowStatus
                                                                            ? "text-disabled"
                                                                            : ""
                                                                    }`}
                                                                    disabled={
                                                                        !filterShowStatus
                                                                    }
                                                                    value={
                                                                        item.value
                                                                    }
                                                                    error={
                                                                        showValidationErrors &&
                                                                        isEmptyValue(
                                                                            item.value,
                                                                        )
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        setFilterList(
                                                                            (
                                                                                prevFilterList,
                                                                            ) =>
                                                                                prevFilterList.map(
                                                                                    (
                                                                                        item,
                                                                                        i,
                                                                                    ) =>
                                                                                        i ===
                                                                                        index
                                                                                            ? {
                                                                                                  ...item,
                                                                                                  value: e
                                                                                                      ?.target
                                                                                                      ?.value,
                                                                                              }
                                                                                            : item,
                                                                                ),
                                                                        );
                                                                        if (
                                                                            isWizard
                                                                        ) {
                                                                            const updatedFilterList =
                                                                                [
                                                                                    ...filterList,
                                                                                ].map(
                                                                                    (
                                                                                        item,
                                                                                        i,
                                                                                    ) =>
                                                                                        i ===
                                                                                        index
                                                                                            ? {
                                                                                                  ...item,
                                                                                                  value: e
                                                                                                      ?.target
                                                                                                      ?.value,
                                                                                              }
                                                                                            : item,
                                                                                );
                                                                            const filteredComparings =
                                                                                filterComparings(
                                                                                    updatedFilterList,
                                                                                );
                                                                            props?.handleSave(
                                                                                {
                                                                                    operator:
                                                                                        selectedOperator,
                                                                                    comparings:
                                                                                        filteredComparings,
                                                                                },
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        </div>
                                                    )}
                                                    <DeleteCol className="col-md-1 col-sm-12">
                                                        <IconButton
                                                            size="small"
                                                            disabled={
                                                                !filterShowStatus
                                                            }
                                                            onClick={() =>
                                                                handleDeleteRow(
                                                                    index,
                                                                )
                                                            }
                                                            aria-label="Remove filter"
                                                            sx={
                                                                connectionSectionDeleteButtonSx
                                                            }
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </DeleteCol>
                                                </div>
                                            </MobileFilterRow>
                                        </FilterListItem>
                                    ))}

                                {filterShowStatus && (
                                    <FilterListItem className="filter-list-item">
                                        <div className="row">
                                            <div className="col-sm-12">
                                                <StandartButton
                                                    type="button"
                                                    variant={
                                                        ButtonVariant.BLACK_OUTLINED
                                                    }
                                                    onClick={
                                                        handleFilterAddItem
                                                    }
                                                    startIcon={
                                                        <AddRoundedIcon />
                                                    }
                                                >
                                                    {language
                                                        ?.lead_connection_filter
                                                        ?.add_row ?? "Add Row"}
                                                </StandartButton>
                                            </div>
                                        </div>
                                    </FilterListItem>
                                )}
                            </FilterListLeft>

                            <FilterListRight>
                                <FormControl style={{ width: "100%" }}>
                                    <Select
                                        label={
                                            language?.lead_connection_filter
                                                ?.operator ?? "Operator"
                                        }
                                        onChange={(e) => {
                                            setSelectedOperator(
                                                e?.target?.value,
                                            );
                                            if (isWizard) {
                                                const filteredComparings =
                                                    filterComparings(
                                                        filterList,
                                                    );
                                                props?.handleSave({
                                                    operator: e?.target?.value,
                                                    comparings:
                                                        filteredComparings,
                                                });
                                            }
                                        }}
                                        value={selectedOperator}
                                        disabled={
                                            !filterShowStatus ||
                                            (filterList &&
                                                filterList.length < 2)
                                        }
                                        className="w-full"
                                        error={
                                            showValidationErrors &&
                                            filterList.length > 1 &&
                                            selectedOperator === ""
                                        }
                                    >
                                        {props?.operators &&
                                            props?.operators.map((item) => (
                                                <MenuItem
                                                    key={item.code}
                                                    value={item.code}
                                                >
                                                    {item.description?.toUpperCase()}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </FilterListRight>

                            {filterShowStatus && !isWizard && (
                                <SaveButtonWrapper>
                                    {props?.isSaveLoading && (
                                        <div className="circle-loader">
                                            <ClipLoader
                                                color={"#000"}
                                                size={17}
                                            />
                                        </div>
                                    )}
                                    <SaveButton
                                        type="button"
                                        onClick={handleSave}
                                        color="primary"
                                        variant="contained"
                                    >
                                        {language?.lead_connection_filter
                                            ?.save_changes ?? "Save Changes"}
                                    </SaveButton>
                                </SaveButtonWrapper>
                            )}
                        </FilterListWrapper>
                    </div>
                </>
            )}
        </FilterArea>
    );
};

export default LeadConnectionFilter;
